/**
 * Os campos das toolbars do SM: busca, `select`, data e o rótulo do período.
 *
 * Papel: reproduz `.toolbar input[type=text]`, `.toolbar select`,
 * `.toolbar input[type=date]` e `.toolbar .date-label` (l. 163–191, 252–261).
 *
 * Como usar: nenhum campo declara largura — fica a largura padrão do navegador,
 * como na origem. O `FiltroDePeriodo` da base desenha os próprios campos de
 * data e recebe as classes daqui por `classeDoCampo={CLASSE_DA_DATA_DA_BARRA}` e
 * `classeDoRotulo={CLASSE_DO_ROTULO_DA_BARRA}`; `DataDaBarra` e `RotuloDaBarra`
 * servem a quem desenha o campo à mão. A seta do `select` vem de
 * `--emb-seta-select`, no `index.css`.
 */
import { type ComponentProps, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

/** A busca e o `select` da toolbar. */
const CLASSE_DO_CAMPO_DA_BARRA =
  'font-outfit appearance-none rounded-[6px] border border-emb-borda bg-superficie px-2.5 py-2 text-[12px] font-normal text-texto-fraco focus:border-foreground focus:outline-none';

/** O campo de data da toolbar, para o `classeDoCampo` do `FiltroDePeriodo`. */
export const CLASSE_DA_DATA_DA_BARRA =
  'font-outfit rounded-[6px] border border-emb-borda bg-superficie px-2 py-[7px] text-[11px] font-normal text-texto-fraco';

/** O texto "… de" e "até" do período, para o `classeDoRotulo` do `FiltroDePeriodo`. */
export const CLASSE_DO_ROTULO_DA_BARRA =
  'font-outfit text-[11px] font-normal tracking-[0.5px] whitespace-nowrap text-texto-fraco uppercase';

/**
 * A busca da toolbar.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O campo de texto.
 */
export function BuscaDaBarra({ className, ...props }: ComponentProps<'input'>) {
  return <input type="text" className={cn(CLASSE_DO_CAMPO_DA_BARRA, className)} {...props} />;
}

/**
 * O `select` de escolha única da toolbar, com a seta da origem.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `select`.
 */
export function SelectDaBarra({ className, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        CLASSE_DO_CAMPO_DA_BARRA,
        'bg-[image:var(--emb-seta-select)] bg-[position:right_10px_center] bg-no-repeat pr-7',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Um campo de data da toolbar.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O campo `type="date"`.
 */
export function DataDaBarra({ className, ...props }: ComponentProps<'input'>) {
  return <input type="date" className={cn(CLASSE_DA_DATA_DA_BARRA, className)} {...props} />;
}

/**
 * O texto pequeno em caixa alta ao lado das datas.
 *
 * @param children - O texto, como a origem o escreve.
 * @returns O texto.
 */
export function RotuloDaBarra({ children }: { children: ReactNode }) {
  return <span className={CLASSE_DO_ROTULO_DA_BARRA}>{children}</span>;
}
